/**
 * The five learner statuses, as words.
 *
 * This module exists before any screen that would use it, because it is the
 * one place where a status could become a number and it is cheaper to close
 * that door while it is still empty. CLAUDE.md: "Five statuses, exactly. No
 * percentage, anywhere, ever — not in the API, not in the UI, not in a
 * tooltip." `status-labels.test.ts` asserts the count, asserts that no label
 * or description contains a digit or a percent sign, and asserts that the keys
 * are exactly `LEARNER_STATUSES` from `@davinci/domain` — so a sixth status
 * cannot be introduced here, and one added to the domain cannot be silently
 * ignored here.
 *
 * A note on the thing this guards against, from issue #5: a progress bar is a
 * percentage wearing a costume. So is a five-segment meter, a ring, a score
 * out of five, and a sort that reads as a ranking. HYP-UX-003's registered
 * falsification condition explicitly forbids resolving a refutation by adding
 * a number — if the founder cannot tell these five apart, the answer is better
 * words, not a gauge.
 *
 * `mixed_evidence` is deliberately absent. It is a note attached to a status,
 * never a status, so it will arrive as its own field on a status envelope and
 * must never be added to this map.
 *
 * These are UI-language strings. §8 keeps UI language, target language and
 * support language as three separate settings, and messages are complete
 * sentences rather than concatenated fragments — a label and its description
 * are two whole strings here for exactly that reason.
 */

import { LEARNER_STATUSES, type LearnerStatus } from '@davinci/domain';
import type { UiLocale } from './locale.js';

export interface StatusLabel {
  /** What the learner sees. Never abbreviated to an initial or a symbol. */
  readonly label: string;
  /** One sentence saying what evidence produced it. */
  readonly description: string;
}

export const STATUS_LABELS: Readonly<Record<LearnerStatus, StatusLabel>> = {
  not_tested: {
    label: 'Not tested',
    description: 'Nothing has been asked about this yet.',
  },
  learning: {
    label: 'Learning',
    description: 'It has been attempted, and the evidence so far does not show use.',
  },
  can_use_with_help: {
    label: 'Can use with help',
    description: 'Used correctly after answer-revealing help, so the attempt was not independent.',
  },
  can_use_independently: {
    label: 'Can use independently',
    description: 'Used correctly with no answer-revealing help beforehand.',
  },
  retained: {
    label: 'Retained',
    description: 'Used independently again after a delay, and the exact delay is recorded.',
  },
};

/** The Hebrew set (#445). Same five keys, same rule: words, never a number or a gauge. */
export const STATUS_LABELS_HE: Readonly<Record<LearnerStatus, StatusLabel>> = {
  not_tested: {
    label: 'עוד לא נבדק',
    description: 'עוד לא נשאלה שאלה על הנושא הזה.',
  },
  learning: {
    label: 'בלמידה',
    description: 'היה ניסיון, ועד כה הראיות אינן מראות שימוש בנושא.',
  },
  can_use_with_help: {
    label: 'אפשר להשתמש עם עזרה',
    description: 'השימוש היה נכון אחרי עזרה שחשפה את התשובה, ולכן הניסיון לא היה עצמאי.',
  },
  can_use_independently: {
    label: 'שימוש עצמאי',
    description: 'השימוש היה נכון בלי עזרה שחשפה את התשובה קודם לכן.',
  },
  retained: {
    label: 'נשמר לאורך זמן',
    description: 'השימוש היה עצמאי שוב אחרי הפסקה, והזמן שעבר נרשם במדויק.',
  },
};

export const STATUS_LABELS_BY_LOCALE: Readonly<Record<UiLocale, Readonly<Record<LearnerStatus, StatusLabel>>>> = {
  en: STATUS_LABELS,
  he: STATUS_LABELS_HE,
};

/** Total, so a status can never render as a blank or as its raw identifier. */
export function statusLabel(status: LearnerStatus, locale: UiLocale = 'en'): StatusLabel {
  return STATUS_LABELS_BY_LOCALE[locale][status];
}

/** Presentation order. Weakest evidence first; it is not a score. */
export const STATUS_DISPLAY_ORDER: readonly LearnerStatus[] = LEARNER_STATUSES;
